import {
  DataService,
  PamlabApiServiceService,
  RouteService,
} from '@/shared/services';
import { Component, OnInit } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { Subscription } from 'rxjs';
import { TransferStudentReturn } from 'src/server/types';
import * as _ from 'lodash-es';
import { Dictionary } from 'lodash';
import { StudentForm } from '@/core/interfaces/transfer-student';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
  selector: 'transfer-enrollment-trends-content',
  templateUrl: './transfer-enrollment-trends-content.component.html',
  styleUrl: './transfer-enrollment-trends-content.component.css',
  standalone: false,
})
export class TransferEnrollmentTrendsContentComponent {
  private subscription = new Subscription();
  public transferStudents!: TransferStudentReturn[];
  public selectedFilters!: StudentForm;
  public enrollmentTrendsChart!: Chart;
  public retentionTrendsChart!: Chart;
  public graduationRatesChart!: Chart;

  constructor(
    public dataService: DataService,
    private apiService: PamlabApiServiceService,
    private routeService: RouteService,
  ) {
    Chart.register(...registerables, ChartDataLabels);
  }

  ngOnInit(): void {
    this.dataService.setPendingData(true);
    this.apiService.fetchTransferEnrollmentTrends().subscribe({
      next: (data) => {
        this.transferStudents = data;
        this.setEnrollmentTrendsChart();
        this.setRetentionTrendsChart();
        this.setGraduationRatesChart();
        this.dataService.setPendingData(false);
      },
    });

    this.subscription.add(
      this.dataService.getTransferStudentsFilters().subscribe({
        next: (filters) => {
          this.selectedFilters = filters as StudentForm;
          this.setRetentionTrendsChart();
          this.setGraduationRatesChart();
        },
      }),
    );
  }

  private setEnrollmentTrendsChart() {
    this.enrollmentTrendsChart && this.enrollmentTrendsChart.destroy();

    const groupedByCohortsWithUniqStudents = _.groupBy(
      this.transferStudents,
      'cohort',
    );
    // Extract first fall in each cohort
    Object.keys(groupedByCohortsWithUniqStudents).forEach((key) => {
      groupedByCohortsWithUniqStudents[key] = groupedByCohortsWithUniqStudents[
        key
      ].filter((x) => x.nth_fall === 'Year1Fall');
    });
    const cohorts = Object.keys(groupedByCohortsWithUniqStudents);

    setTimeout(() => {
      this.enrollmentTrendsChart = new Chart('enrollment-trends-chart', {
        type: 'line',
        data: {
          labels: cohorts,
          datasets: [
            {
              label: 'Total Head Count',
              data: Object.values(groupedByCohortsWithUniqStudents).map(
                (students) => students.length,
              ),
            },
            {
              label: 'Florida College System (FCS)',
              data: Object.values(groupedByCohortsWithUniqStudents).map(
                (students) =>
                  students.filter(
                    (s) =>
                      s.admit_type === 'Transfer From Florida College System',
                  ).length,
              ),
            },
            {
              label: 'Other Undergraduate',
              data: Object.values(groupedByCohortsWithUniqStudents).map(
                (students) =>
                  students.filter(
                    (s) => s.admit_type === 'Other Undergraduate Transfer',
                  ).length,
              ),
            },
            {
              label: 'Associate in Arts (AA)',
              data: Object.values(groupedByCohortsWithUniqStudents).map(
                (students) =>
                  students.filter(
                    (s) => s.admit_deg_type === 'Associate in Arts (AA)',
                  ).length,
              ),
            },
            {
              label: 'Associate in Science (AS)',
              data: Object.values(groupedByCohortsWithUniqStudents).map(
                (students) =>
                  students.filter(
                    (s) => s.admit_deg_type === 'Associate in Science (AS)',
                  ).length,
              ),
            },
            {
              label: 'Full-Time',
              data: Object.values(groupedByCohortsWithUniqStudents).map(
                (students) =>
                  students.filter((s) => s.full_part_time_ind === 'F').length,
              ),
            },
            {
              label: 'Part-Time',
              data: Object.values(groupedByCohortsWithUniqStudents).map(
                (students) =>
                  students.filter((s) => s.full_part_time_ind === 'P').length,
              ),
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            datalabels: {
              display: false,
              color: '#000', // Text color
              anchor: 'end', // Position relative to bar (start, center, end)
              align: 'top', // Align text (top, middle, bottom)
            },
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Cohort',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
            y: {
              beginAtZero: true,
              display: true,
              title: {
                display: true,
                text: 'Count',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
          },
        },
      });
      this.enrollmentTrendsChart.update();
    });
  }

  private setRetentionTrendsChart() {
    this.retentionTrendsChart && this.retentionTrendsChart.destroy();
    const filteredData = this.applyFilters(this.transferStudents);
    const groupedByCohorts = _.groupBy(filteredData, 'cohort');
    const numStudentsByCohorts = _.mapValues(groupedByCohorts, (items) =>
      _.uniqBy(items, 'uwf_id'),
    );
    const retentionData = this.processFallToFallRetentionData(groupedByCohorts);
    const totalList = Object.values(numStudentsByCohorts).map((x) => x.length);
    const dataService = this.dataService;

    setTimeout(() => {
      this.retentionTrendsChart = new Chart('retention-trends-chart', {
        type: 'bar',
        data: {
          labels: Object.keys(groupedByCohorts),
          datasets: Object.entries(retentionData).map(([label, dataValues]) => {
            return {
              label,
              data: dataValues,
            };
          }),
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            datalabels: {
              color: '#000', // Text color
              anchor: 'end', // Position relative to bar (start, center, end)
              align: 'top', // Align text (top, middle, bottom)
              formatter: function (value, context) {
                return `${value}\n${dataService.round(value / totalList[context.dataIndex])}%`;
              },
            },
            legend: {
              labels: {
                font: {
                  size: 15,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Cohort',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
            y: {
              display: true,
              title: {
                display: true,
                text: 'Count',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
          },
        },
      });
      this.retentionTrendsChart.update();
    });
  }

  private setGraduationRatesChart() {
    this.graduationRatesChart && this.graduationRatesChart.destroy();

    const groupedByCohorts = _.groupBy(this.transferStudents, 'cohort');
    const graduationData = this.processGraduationData(groupedByCohorts);
    const numGrads = Object.values(graduationData).map(
      (value) => value.num_grads,
    );

    // Trim right of the array if values are 0s
    const numGradPercentages = Object.values(graduationData).map(
      (value) => value.percentage,
    );
    const trimmedNumGrads = _.dropRightWhile(numGrads, (num) => num === 0);
    const trimmedNumGradPercentages = _.dropRightWhile(
      numGradPercentages,
      (num) => num === 0,
    );
    let cohorts = Object.keys(groupedByCohorts);
    cohorts = cohorts.slice(0, trimmedNumGrads.length);
    const recent4Years = ['2018', '2019', '2020', '2021'];
    let totalRecent4YearPct = 0;
    let numToAvg = 0;

    recent4Years.forEach((year) => {
      if (graduationData[year].percentage !== null) {
        totalRecent4YearPct += graduationData[year].percentage;
        numToAvg++;
      }
    });

    const recent4yrsAvg = this.dataService.round(
      totalRecent4YearPct / numToAvg / 100,
    );
    const recent4yrsAvgData: (number | null)[] = [];

    Object.keys(graduationData).forEach((key) => {
      if (recent4Years.includes(key)) {
        recent4yrsAvgData.push(recent4yrsAvg);
      } else {
        recent4yrsAvgData.push(null);
      }
    });

    setTimeout(() => {
      this.graduationRatesChart = new Chart('graduation-rates-chart', {
        data: {
          labels: cohorts,
          datasets: [
            {
              label: 'Graduate Count',
              data: trimmedNumGrads,
              backgroundColor: 'rgba(0, 156, 222, 0.6)',
              borderColor: 'rgba(0, 156, 222, 1)',
              borderWidth: 1,
              yAxisID: 'yLeft',
              type: 'bar',
            },
            {
              label: 'Pass Rate',
              data: trimmedNumGradPercentages,
              backgroundColor: 'rgba(153, 102, 255, 0.6)',
              borderColor: 'rgba(153, 102, 255, 1)',
              borderWidth: 2,
              type: 'line',
              yAxisID: 'yRight',
              fill: false,
              spanGaps: true,
            },
            {
              label: `Recent 4-yrs avg: ${recent4yrsAvg}%`,
              data: recent4yrsAvgData,
              backgroundColor: 'rgba(179, 0, 0, 0.6)',
              borderColor: 'rgba(179, 0, 0, 1)',
              borderWidth: 2,
              type: 'line',
              fill: false,
              yAxisID: 'yRight',
              spanGaps: true,
              pointRadius: 0, // Hides the circle dots
              pointHoverRadius: 0, // Hides hover dots
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label;
                  const value = context.formattedValue;
                  return context.dataset.type === 'line'
                    ? `${label}: ${value}%`
                    : `${label}: ${value}`;
                },
              },
            },
            datalabels: {
              color: '#000', // Text color
              anchor: 'center', // Position relative to bar (start, center, end)
              align: 'bottom', // Align text (top, middle, bottom)
              formatter(value, context) {
                if (value === 0) return '';

                if (context.dataset.type !== 'line') return value;

                const shouldHideLabel =
                  context.dataset.label?.includes('Recent 4-yrs avg');

                return shouldHideLabel ? null : `${value}%`;
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Cohort',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
            yLeft: {
              beginAtZero: true,
              position: 'left',
              ticks: {
                callback: function (value) {
                  return Number.isInteger(value) ? value : null; // Show only whole numbers
                },
              },
              title: {
                display: true,
                text: 'Graduate Count',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
            yRight: {
              beginAtZero: true,
              position: 'right',
              title: {
                display: true,
                text: 'Pass Rate',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
          },
        },
      });
      this.graduationRatesChart.update();
    });
  }

  private processFallToFallRetentionData(
    data: Dictionary<TransferStudentReturn[]>,
  ) {
    const results: {
      'Year 1 Fall': number[];
      'Year 2 Fall': number[];
      'Year 3 Fall': number[];
    } = { 'Year 1 Fall': [], 'Year 2 Fall': [], 'Year 3 Fall': [] };

    Object.entries(data).forEach(([_cohort, students]) => {
      const groupedByFalls = _.groupBy(students, 'demo_time_frame');
      const keys = Object.keys(groupedByFalls);
      if (keys.at(0)) {
        results['Year 1 Fall'].push(groupedByFalls[keys.at(0)!].length);
      }

      if (keys.at(1)) {
        results['Year 2 Fall'].push(groupedByFalls[keys.at(1)!].length);
      }

      if (keys.at(2)) {
        results['Year 3 Fall'].push(groupedByFalls[keys.at(2)!].length);
      }
    });

    return results;
  }

  private processGraduationData(data: Dictionary<TransferStudentReturn[]>) {
    const cohorts = Object.keys(data);
    cohorts.forEach((cohort) => {
      data[cohort] = data[cohort].filter((x) => x.nth_fall === 'Year1Fall');
    });

    const results: {
      [cohort: string]: {
        num_grads: number;
        percentage: number | null; // Null means no students available
      };
    } = {};

    Object.entries(data).forEach(([cohort, students]) => {
      const filteredData = this.applyFilters(students);
      const numGrads = this.applyGraduationTimeFilter(filteredData).length;

      results[cohort] = {
        num_grads: numGrads,
        percentage: filteredData.length
          ? this.dataService.round(numGrads / filteredData.length)
          : null,
      };
    });

    return results;
  }

  private applyFilters(filteredData: TransferStudentReturn[]) {
    if (!this.selectedFilters) return filteredData;

    if (this.selectedFilters.admit_type !== 'all') {
      filteredData = filteredData.filter(
        (x) => x.admit_type === this.selectedFilters.admit_type,
      );
    }

    if (this.selectedFilters.associate_degree !== 'all') {
      filteredData = filteredData.filter(
        (x) => x.admit_deg_type === this.selectedFilters.associate_degree,
      );
    }

    if (this.selectedFilters.full_part_time_ind !== 'all') {
      filteredData = filteredData.filter(
        (x) => x.full_part_time_ind === this.selectedFilters.full_part_time_ind,
      );
    }

    if (this.selectedFilters.college !== 'all') {
      filteredData = filteredData.filter(
        (x) => x.college === this.selectedFilters.college,
      );
    }

    if (this.selectedFilters.department !== 'all') {
      filteredData = filteredData.filter(
        (x) => x.department === this.selectedFilters.department,
      );
    }

    if (this.selectedFilters.program !== 'all') {
      filteredData = filteredData.filter(
        (x) => x.program === this.selectedFilters.program,
      );
    }

    if (this.selectedFilters.transfer_college_name !== 'all') {
      filteredData = filteredData.filter(
        (x) =>
          x.transfer_college_name ===
          this.selectedFilters.transfer_college_name,
      );
    }

    return filteredData;
  }

  private applyGraduationTimeFilter(filteredData: TransferStudentReturn[]) {
    // Default
    if (!this.selectedFilters) {
      filteredData = filteredData.filter((x) => x.grad_3yr === 'three-years');
      return filteredData;
    }

    if (this.selectedFilters.graduation_time === 'two-years') {
      filteredData = filteredData.filter(
        (x) => x.grad_2yr === this.selectedFilters.graduation_time,
      );
    }

    if (this.selectedFilters.graduation_time === 'three-years') {
      filteredData = filteredData.filter(
        (x) => x.grad_3yr === this.selectedFilters.graduation_time,
      );
    }

    return filteredData;
  }

  navigateToGlossaryOfTerms(termValue: string) {
    this.routeService.navigateNewTab('/glossary-of-terms', {
      term_value: termValue,
    });
  }
}
